import {
  IProductNutritionValue,
  ProductNutritionCategory
} from "src/core/products/api/products.api";
import { ProductNutritionValuesTableRowProps } from "src/core/products/components/ProductNutritionValuesTable/ProductNutritionValuesTableRow";

/**
 * Takes nutritionValues and transforms data to fit required format the ProductNutritionValuesTableRow expects
 * @param nutritionValues
 */
function transformNutritionData(nutritionValues: IProductNutritionValue[]): {
  energy: Pick<ProductNutritionValuesTableRowProps, "per100" | "perItem"> | null,
  nutritionData: ProductNutritionValuesTableRowProps[]
} {
  // don't run if no values passed
  if (nutritionValues?.length === 0) {
    return {
      energy: null,
      nutritionData: []
    };
  }

  const keyedNutritionData = {};
  const keyedEnergyData = {};
  const keyedCarbAndSugars = {};
  const keyedFatAndSaturates = {};

  // populate keyed objects
  nutritionValues.forEach(nutritionalValue => {
    const category = nutritionalValue.category;
    const unitLabel = nutritionalValue.unit === "item" ? "perItem" : "per100";

    // handle keyedCarbAndSugars
    if (category === ProductNutritionCategory.Sugars || category === ProductNutritionCategory.Carbs) {
      if (keyedCarbAndSugars[ category ]) {
        keyedCarbAndSugars[ category ][ unitLabel ] = nutritionalValue.value;
      } else {
        keyedCarbAndSugars[ category ] = {
          category,
          [ unitLabel ]: nutritionalValue.value,
          measurement: nutritionalValue.measurement
        };
      }

      return;
    }

    // handle keyedFatAndSaturates
    if (category === ProductNutritionCategory.Fat || category === ProductNutritionCategory.Saturates) {
      if (keyedFatAndSaturates[ category ]) {
        keyedFatAndSaturates[ category ][ unitLabel ] = nutritionalValue.value;
      } else {
        keyedFatAndSaturates[ category ] = {
          category,
          [ unitLabel ]: nutritionalValue.value,
          measurement: nutritionalValue.measurement
        };
      }

      return;
    }

    // handle keyedEnergyData
    if (category === ProductNutritionCategory.KJ || category === ProductNutritionCategory.KCal) {
      if (keyedEnergyData[ category ]) {
        keyedEnergyData[ category ][ unitLabel ] = nutritionalValue.value;
      } else {
        keyedEnergyData[ category ] = {
          category,
          [ unitLabel ]: nutritionalValue.value,
          measurement: nutritionalValue.measurement
        };
      }

      return;
    }

    // handle all other categories
    if (keyedNutritionData[ category ]) {
      keyedNutritionData[ category ][ unitLabel ] = nutritionalValue.value;
    } else {
      keyedNutritionData[ category ] = {
        category,
        [ unitLabel ]: nutritionalValue.value,
        measurement: nutritionalValue.measurement
      };
    }
  });

  // generate general nutritionData to render,
  // placing carbs & sugars followed by fat & saturates at the beginning of nutritionData
  const nutritionData: ProductNutritionValuesTableRowProps[] = [];

  if (keyedCarbAndSugars[ ProductNutritionCategory.Carbs ] && keyedCarbAndSugars[ ProductNutritionCategory.Sugars ]) {
    nutritionData.push(keyedCarbAndSugars[ ProductNutritionCategory.Carbs ]);
    nutritionData.push(keyedCarbAndSugars[ ProductNutritionCategory.Sugars ]);
  }

  if (keyedFatAndSaturates[ ProductNutritionCategory.Fat ] && keyedFatAndSaturates[ ProductNutritionCategory.Saturates ]) {
    nutritionData.push(keyedFatAndSaturates[ ProductNutritionCategory.Fat ]);
    nutritionData.push(keyedFatAndSaturates[ ProductNutritionCategory.Saturates ]);
  }

  for (const category in keyedNutritionData) {
    nutritionData.push(keyedNutritionData[ category ]);
  }

  // generate energy data to render
  const kj = keyedEnergyData[ ProductNutritionCategory.KJ ];
  const kcal = keyedEnergyData[ ProductNutritionCategory.KCal ];

  const energy = {
    per100: `${kj.per100}${kj.measurement}/${kcal.per100}${kcal.measurement}`,
    perItem: `${kj.perItem}${kj.measurement}/${kcal.perItem}${kcal.measurement}`
  };

  return {
    energy,
    nutritionData
  };
}

export { transformNutritionData };
